// Share client. Mirrors middleware/routes/share.py.

import type { ChartSpec } from "../features/charts/types";
import { api } from "./base";

export interface ShareResult {
  ok: true;
  share_token: string;
  share_url: string;
}

export interface SharedChartPayload {
  kind: "chart";
  chart_spec: ChartSpec;
  user_query: string;
  organization?: string;
  datasource_id?: string | null;
  created_at?: string | null;
}

export interface SharedDashboardPayload {
  kind: "dashboard";
  dashboard_id: number;
  name: string;
  organization: string;
  insights: { user_query: string; chart_spec: ChartSpec }[];
  created_at?: string | null;
}

export type SharedPayload = SharedChartPayload | SharedDashboardPayload;

async function jsonFetch<T>(bearer: string | null, url: string, init?: RequestInit): Promise<T> {
  const resp = await fetch(url, {
    ...init,
    headers: {
      ...(init?.headers ?? {}),
      ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}),
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
    },
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    throw new Error(`${init?.method ?? "GET"} ${url} → ${resp.status}: ${text}`);
  }
  return (await resp.json()) as T;
}

export function shareChart(
  bearer: string,
  args: { chart_spec: ChartSpec; user_query: string; organization: string; datasource_id?: string },
): Promise<ShareResult> {
  return jsonFetch<ShareResult>(bearer, api("/share/chart"), {
    method: "POST",
    body: JSON.stringify({ ...args, access_type: "public" }),
  });
}

export function shareDashboard(bearer: string, dashboard_id: number, access_type: "public" | "org" = "org"): Promise<ShareResult> {
  return jsonFetch<ShareResult>(bearer, api("/share/dashboard"), {
    method: "POST",
    body: JSON.stringify({ dashboard_id, access_type }),
  });
}

export function fetchShared(token: string): Promise<SharedPayload> {
  // Public route — no bearer required.
  return jsonFetch<SharedPayload>(null, `${api("/share")}/${encodeURIComponent(token)}`);
}
